'use client'

import { ReactNode } from 'react'
import { useQuery } from '@tanstack/react-query'
import ProductsBreadcrumb from './products-breadcrumb'
import ProductsCategoryNav from './products-category-nav'
import { ProductsBreadcrumbSkeleton, ProductsSliderSkeleton } from '@/components/partials'
import { fetchCategoriesTree } from '@/lib/api'
import { CategoryType } from '@/types/category'

type PropsType = {
  children: ReactNode
}

/**
 * Client-side category navigation for the products' layout.
 *
 * The category tree is fetched on the client so there is always a loading state: on the first render
 * (SSR / hard refresh) React Query is pending, so the breadcrumb + slider skeletons render into the
 * HTML; on the client the tree loads and the real nav appears. Kept in the (preserved) layout, so it
 * stays mounted across category navigations — the tree is cached, no skeleton flashes again, and only
 * the active highlight updates from the pathname. The products below (`children`) stay server-rendered
 * for SEO, and the SEO breadcrumb (JSON-LD) is rendered server-side in the page.
 */
const ProductsCategoryNavClient = ({ children }: PropsType) => {
  const { data: categories = [], isPending } = useQuery<CategoryType[]>({
    queryKey: ['products-nav-categories'],
    queryFn: async () => {
      // Use the tree endpoint (via the shared axios client, like the other client hooks) so each
      // category carries its nested `children` — the slider's subcategory row derives them from it.
      const categoriesTree = await fetchCategoriesTree({ active: true })
      return categoriesTree.filter(category => category.active)
    },
    staleTime: 5 * 60_000
  })

  return (
    <>
      {isPending ? <ProductsBreadcrumbSkeleton /> : <ProductsBreadcrumb categories={categories} />}

      <div className="container mx-auto flex flex-col gap-5 px-4 py-8">
        {isPending ? <ProductsSliderSkeleton /> : <ProductsCategoryNav categories={categories} />}

        {children}
      </div>
    </>
  )
}

export default ProductsCategoryNavClient
